/* eslint react-hooks/immutability: ["warn"] */
import { useState } from "react";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useTranslation } from "@coworker/locales";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { Media } from "../types/media";
import { MediaTypes } from "../constants";
import { MediaSettingsForm } from "../Organisms/MediaSettingsForm";
import { FullWidthButton } from "../../SkapaButton";
import { useCreateMedia } from "../hooks/useCreateMedia";
import { useMediasByStoreId } from "../hooks/useMediasByStoreId";
import { useStoreId } from "../../../core/auth/useLoggedInUser";
import {
  checkEmptyMediaName,
  checkMediaName,
} from "../Organisms/Settings/SettingsForm.helper";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { getMediaTypeText, getRequiredFields } from "../tools";

const SUBMITTING_RESET_TIMEOUT_MS = 300;

export function CreateMediaView() {
  const createMediaMutation = useCreateMedia();
  const storeId = useStoreId();
  const { t } = useTranslation();
  const navigate = useFixaNavigate();

  const [currentMedia, setCurrentMedia] = useState<Media>({
    mediaType: MediaTypes.NONE,
    storeId: storeId,
    active: "INACTIVE",
  } as Media);
  const [currentCheckedMediaType, setCurrentCheckedMediaType] =
    useState<MediaTypes>(MediaTypes.NONE);
  const [mediaIsUpdated, setMediaIsUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: mediasResponse } = useMediasByStoreId(storeId);

  const linkbar = (
    <Linkbar
      currentLabel={t("addMediaString")}
      items={[
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
      ]}
    />
  );

  const showPleaseFillInMandatoryFields =
    !isSubmitting &&
    (!mediasResponse ||
      checkEmptyMediaName(currentMedia) ||
      checkMediaName(currentMedia, mediasResponse.medias || []) ||
      !mediaIsUpdated ||
      !validateMediaFields(currentMedia));

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const mediaId = await createMediaMutation.mutateAsync(currentMedia);
      if (!mediaId) {
        alert(t("searchErrorString"));
        return;
      }
      navigate(
        `/mediamaintenance/tabs/${mediaId.mediaId}?tab=article-list-tab`
      );
    } catch {
      alert(t("searchErrorString"));
    } finally {
      // Not the best solution but timeout is needed to prevent the "Please fill in mandatory fields"
      // message from flashing before navigation occurs
      setTimeout(() => {
        setIsSubmitting(false);
      }, SUBMITTING_RESET_TIMEOUT_MS);
    }
  };

  function validateMediaFields(media: Media): boolean {
    const requiredKeys = getRequiredFields(media);
    return requiredKeys.every((key) => {
      const value = media[key as keyof Media];
      return value;
    });
  }

  return (
    <FullScreenPopup
      noPadding
      linkbar={linkbar}
      fullHeightContent={true}
      fieldsAreEmpty={showPleaseFillInMandatoryFields}
      actionBarContent={
        <FullWidthButton
          disabled={showPleaseFillInMandatoryFields || isSubmitting}
          type="primary"
          text={t("createNameString", {
            name: getMediaTypeText(currentMedia.mediaType as MediaTypes),
          })}
          onClick={onSubmit}
        />
      }
    >
      <MediaSettingsForm
        currentMedia={currentMedia}
        medias={mediasResponse?.medias || []}
        setCurrentMedia={(newMedia) => {
          setMediaIsUpdated(true);
          setCurrentMedia(newMedia);
        }}
        currentCheckedMediaType={currentCheckedMediaType}
        setCurrentCheckedMediaType={setCurrentCheckedMediaType}
        isUpdate={false}
      />
    </FullScreenPopup>
  );
}
